import { useQuery } from '@tanstack/react-query';
import { isStaffRole } from '@pazportal/types';
import { useAuth } from '../contexts/AuthContext';
import { readMrcCollectorIncidents } from '../lib/readMrcCollectorIncidents';
import { CollectorIncidentError, MRC_INCIDENT_LOCATION, type MrcCollectorIncidents } from '../lib/mrcCollectorIncidents';

const labels = {
  collector_heartbeat_unavailable: 'Collector heartbeat unavailable',
  collector_spool_pressure: 'Collector storage pressure',
  source_unavailable: 'Collection source unavailable',
};
const stateLabels = { pending: 'Pending', firing: 'Firing', recovering: 'Recovering', resolved: 'Resolved' };
const severityLabels = { info: 'Info', warning: 'Warning', critical: 'Critical' };
const stamp = (value: string) => `${new Date(value).toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, '')} UTC`;

export function MrcCollectorIncidentsCard({ locationId }: { locationId: string }) {
  if (locationId !== MRC_INCIDENT_LOCATION) return null;
  if (import.meta.env.VITE_MRC_COLLECTOR_INCIDENTS_ENABLED !== 'true') {
    return <section aria-label="PazNetOps collector incidents" className="rounded-lg border border-border bg-surface p-5">
      <h2 className="font-semibold text-text">Collector incidents</h2>
      <p className="mt-3 text-sm text-text-muted">Collector incident monitoring is unavailable in this deployment. Collection health and coverage are not inferred.</p>
    </section>;
  }
  return <ActiveMrcCollectorIncidentsCard locationId={locationId} />;
}

function ActiveMrcCollectorIncidentsCard({ locationId }: { locationId: string }) {
  const { user, session, role, loading } = useAuth();
  const allowed = !!user && !!session && isStaffRole(role);
  const query = useQuery({
    queryKey: ['admin-mrc-collector-incidents', locationId, user?.id ?? null, role],
    enabled: !loading && allowed && locationId === MRC_INCIDENT_LOCATION,
    queryFn: ({ signal }) => readMrcCollectorIncidents(locationId, signal),
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: 'always',
  });
  if (locationId !== MRC_INCIDENT_LOCATION) return null;
  // A failed background read hides cached records immediately, including after revocation.
  const error = !loading && !allowed ? new CollectorIncidentError('access') : query.error;
  return <MrcCollectorIncidentsView data={error || loading ? undefined : query.data}
    loading={loading || (allowed && query.isPending)} error={error}
    refreshing={query.isFetching} onRefresh={() => void query.refetch()} />;
}

export function MrcCollectorIncidentsView({ data, loading, error, refreshing, onRefresh }: {
  data?: MrcCollectorIncidents;
  loading: boolean;
  error: unknown;
  refreshing?: boolean;
  onRefresh: () => void;
}) {
  let message: string | null = null;
  if (error instanceof CollectorIncidentError && error.kind === 'access') {
    message = 'Current staff access is required. Incident records are hidden until access is verified.';
  } else if (error instanceof CollectorIncidentError && error.kind === 'unconfigured') {
    message = 'Collector incidents are not configured for this location. A verified collector mapping is required.';
  } else if (error) {
    message = 'Collector incident records are unavailable. Refresh to try again.';
  }
  return <section aria-label="PazNetOps collector incidents" className="rounded-lg border border-border bg-surface">
    <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-4">
      <div><h2 className="font-semibold text-text">Collector incidents</h2>
        <p className="text-xs text-text-muted">PazNetOps · Read only</p></div>
      <button type="button" disabled={loading || refreshing} onClick={onRefresh}
        className="min-h-11 rounded-md border border-border px-3 text-sm text-primary disabled:opacity-50">Refresh incidents</button>
    </div>
    {message ? <p role="alert" className="px-5 py-4 text-sm text-text-muted">{message}</p>
      : loading || !data ? <p role="status" className="px-5 py-4 text-sm text-text-muted">Loading collector incidents…</p>
      : <>
        <div className="space-y-1 px-5 py-4 text-sm text-text-muted">
          <p>Fetched {stamp(data.read_at)}.</p>
          <p>Latest rule record: {data.rule_record_updated_at ? stamp(data.rule_record_updated_at) : 'Not available'}.</p>
          <p>Rule record time does not confirm a successful evaluation or complete collection coverage.</p>
          {data.configured_rule_kinds === 0 ? <p>Incident rules are not configured.</p>
            : data.configured_rule_kinds < 3 ? <p>{data.configured_rule_kinds} of 3 incident rule kinds are enabled. Rule coverage is incomplete.</p> : null}
          {data.legacy_assessment_rows_not_included > 0 && <p>{data.legacy_assessment_rows_not_included} legacy assessment {data.legacy_assessment_rows_not_included === 1 ? 'row is' : 'rows are'} excluded from this list.</p>}
          {data.truncated && <p>Showing {data.incidents.length} recent records. Additional incident records are not included.</p>}
        </div>
        {data.incidents.length === 0 ? <p className="border-t border-border px-5 py-4 text-sm text-text-muted">No incident records returned; collection coverage is not proven.</p>
          : <ul className="divide-y divide-divider border-t border-border">{data.incidents.map((incident) => <li key={incident.incident_id} className="space-y-1 px-5 py-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium text-text">{labels[incident.kind]} · {incident.source_label}</p>
              <p className="text-xs font-medium text-text">{severityLabels[incident.severity]} · {stateLabels[incident.state]}</p>
            </div>
            <p className="text-xs text-text-muted">Opened {stamp(incident.opened_at)} · Incident updated {stamp(incident.incident_updated_at)}</p>
            <p className="text-xs text-text-muted">Rule record updated {stamp(incident.rule_record_updated_at)}</p>
            {incident.netops_acknowledged_at && <p className="text-xs text-text-muted">NetOps acknowledgement recorded {stamp(incident.netops_acknowledged_at)}.</p>}
          </li>)}</ul>}
      </>}
  </section>;
}
